import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import Card from "../../components/shared/Card/Card";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import SelectInput from "../../components/shared/Inputs/SelectInput";
import useNotification from "../../components/notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import useTickets from "../../data/useTickets";
import { ICreateTicketRequest, TicketPriority } from "../../models/Ticket";

const priorityOptions = [
  { id: TicketPriority.Low, name: "Low" },
  { id: TicketPriority.Medium, name: "Medium" },
  { id: TicketPriority.High, name: "High" },
  { id: TicketPriority.Urgent, name: "Urgent" },
];

export default function CreateTicket() {
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { userId, customerId } = useAuth();
  const { createTicket, uploadAttachment } = useTickets();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const maxAttachmentSizeBytes = 100 * 1024 * 1024;

  const { register, handleSubmit, setValue, watch } = useForm<ICreateTicketRequest>({
    defaultValues: {
      subject: "",
      description: "",
      priority: TicketPriority.Medium,
    },
  });

  useEffect(() => {
    setInfo({
      name: "Create Support Ticket",
      desc: "Raise a new support request",
    });
  }, [setInfo]);

  const onSubmit = async (data: ICreateTicketRequest) => {
    if (!data.subject?.trim() || !data.description?.trim()) {
      addNotification({
        variant: "warn",
        primaryText: "Please enter a subject and description",
      });
      return;
    }

    const oversizedFile = selectedFiles.find((file) => file.size > maxAttachmentSizeBytes);
    if (oversizedFile) {
      addNotification({
        variant: "warn",
        primaryText: `${oversizedFile.name} exceeds the 100MB limit`,
      });
      return;
    }

    const payload: ICreateTicketRequest = {
      ...data,
      customerId: customerId ?? undefined,
      createdByUserId: userId ?? undefined,
    };

    try {
      const created = await createTicket.mutateAsync(payload);
      const createdTicketId = created?.data?.id;

      if (createdTicketId && selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const fileData = new FormData();
          fileData.append("file", file);
          await uploadAttachment.mutateAsync({ ticketId: createdTicketId, data: fileData });
        }
      }

      addNotification({
        variant: "success",
        primaryText: "Ticket created successfully",
      });
      navigate("/tickets");
    } catch (error: any) {
      addNotification({
        variant: "error",
        primaryText: error?.message || "Failed to create ticket",
      });
    }
  };

  return (
    <Card containerClassName="mt-4">
      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
        <TextInput
          label="Subject"
          register={register}
          registerName="subject"
          maxLength={200}
          required
        />

        <SelectInput
          label="Priority"
          register={register}
          registerName="priority"
          options={priorityOptions}
          setValue={setValue}
          watch={watch}
        />

        <TextInput
          label="Description"
          register={register}
          registerName="description"
          richText
          rows={6}
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700">Attachments</label>
          <input
            type="file"
            multiple
            className="mt-1 block w-full text-sm text-gray-700"
            onChange={(e) => setSelectedFiles(Array.from(e.target.files ?? []))}
          />
          <p className="mt-1 text-xs text-gray-500">Add files to include with this ticket (optional).</p>
          <p className="text-xs text-gray-500">Maximum file size: 100MB per attachment.</p>
          {selectedFiles.length > 0 ? (
            <ul className="mt-2 space-y-1 text-xs text-gray-600">
              {selectedFiles.map((file) => (
                <li key={`${file.name}-${file.lastModified}`}>- {file.name}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="flex justify-end gap-2">
          <ButtonInput
            label="Cancel"
            isSubmit={false}
            classes="!bg-gray-200 !text-gray-700 hover:!bg-gray-300"
            onClick={() => navigate("/tickets")}
          />
          <ButtonInput
            label="Create Ticket"
            isSubmit
            classes=""
            onClick={undefined}
            isLoading={createTicket.isLoading || uploadAttachment.isLoading}
          />
        </div>
      </form>
    </Card>
  );
}
